import { ExclamationTriangleIcon, InfoCircledIcon } from "@radix-ui/react-icons";
import { Box, Flex, Text } from "@radix-ui/themes";
import type { CSSProperties } from "react";
import type { ServiceNotice } from "../../hooks/useServiceNotice";

// Allowlist sanitizer for the admin-authored notice HTML. It parses the string
// with the browser's own parser (an inert <template>, so nothing loads or runs),
// then keeps only these inline tags and attributes and drops everything else —
// scripts, event handlers, comments, and non-http(s)/mailto hrefs included.
// Enough for formatted banners without pulling in a library; keep the secret
// admin-only as defense in depth.
const ALLOWED_TAGS: Record<string, string[]> = {
    B: [],
    STRONG: [],
    I: [],
    EM: [],
    U: [],
    BR: [],
    A: [
        "href",
        "target",
        "rel",
    ],
};
const SAFE_HREF = /^(https?:|mailto:)/i;

function sanitizeNoticeHtml(html: string): string {
    const template = document.createElement("template");
    template.innerHTML = html;

    // Snapshot the tree first, since the loops below mutate it.
    const elements: Element[] = [];
    const comments: Comment[] = [];
    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_COMMENT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        if (node.nodeType === Node.COMMENT_NODE) {
            comments.push(node as Comment);
        } else {
            elements.push(node as Element);
        }
    }

    for (const comment of comments) {
        comment.remove();
    }

    const toUnwrap: Element[] = [];
    for (const el of elements) {
        const allowedAttrs = ALLOWED_TAGS[el.tagName];
        if (!allowedAttrs) {
            // script/style carry no visible text, so drop them whole; any other
            // disallowed tag is unwrapped so its text survives.
            if (el.tagName === "SCRIPT" || el.tagName === "STYLE") {
                el.remove();
            } else {
                toUnwrap.push(el);
            }
            continue;
        }
        for (const attr of Array.from(el.attributes)) {
            if (!allowedAttrs.includes(attr.name.toLowerCase())) {
                el.removeAttribute(attr.name);
            }
        }
        if (el.tagName === "A") {
            const href = el.getAttribute("href");
            if (!href || !SAFE_HREF.test(href.trim())) {
                el.removeAttribute("href");
            }
            el.setAttribute("rel", "noopener noreferrer");
            if (!el.getAttribute("target")) {
                el.setAttribute("target", "_blank");
            }
        }
    }

    for (const el of toUnwrap) {
        const parent = el.parentNode;
        if (!parent) {
            continue;
        }
        while (el.firstChild) {
            parent.insertBefore(el.firstChild, el);
        }
        parent.removeChild(el);
    }

    return template.innerHTML;
}

// No dc-* token covers "warning"/"critical" background+border together (only
// --dc-amber exists, as a text color) or "critical" at all, so this pulls the
// matching Radix scale steps directly — the same escape hatch DashboardShell
// already uses for the active-nav accent (var(--blue-9)).
const SEVERITY_STYLE: Record<
    ServiceNotice["severity"],
    {
        style: CSSProperties;
        Icon: typeof InfoCircledIcon;
    }
> = {
    info: {
        style: {
            backgroundColor: "var(--dc-accent-soft)",
            borderBottom: "1px solid var(--blue-7)",
            color: "var(--blue-11)",
        },
        Icon: InfoCircledIcon,
    },
    warning: {
        style: {
            backgroundColor: "color-mix(in srgb, var(--dc-amber) 14%, transparent)",
            borderBottom: "1px solid color-mix(in srgb, var(--dc-amber) 45%, transparent)",
            color: "var(--dc-amber)",
        },
        Icon: ExclamationTriangleIcon,
    },
    critical: {
        style: {
            backgroundColor: "var(--red-a3)",
            borderBottom: "1px solid var(--red-7)",
            color: "var(--red-11)",
        },
        Icon: ExclamationTriangleIcon,
    },
};

/** Admin-controlled banner (service disruption, maintenance, degraded
 * performance), shown on the dashboard and the sign-in page. DashboardShell
 * mounts it as a sibling ABOVE
 * dc-dashboard-shell (not inside the main content column), so it spans the
 * full viewport width including the sidebar. It sits outside the scrollable
 * area entirely — the outer shell is a fixed-height column with the banner as
 * its first row — so it's permanently pinned above everything with no
 * position:sticky needed. No dismiss control by design — an active incident
 * stays visible for every viewer until an admin clears the service notice. */
export default function ServiceNoticeBanner({ notice }: { notice: ServiceNotice }) {
    const { style, Icon } = SEVERITY_STYLE[notice.severity];

    return (
        <Box
            flexShrink="0"
            px="4"
            py="2"
            style={{
                width: "100%",
                boxSizing: "border-box",
                ...style,
            }}
        >
            <Flex align="center" justify="center" gap="2">
                <Icon
                    aria-hidden="true"
                    style={{
                        flex: "0 0 auto",
                    }}
                />
                <Text size="2" align="center">
                    {/* Admin-authored HTML, allowlist-sanitized: inline formatting + safe links only. */}
                    <span
                        dangerouslySetInnerHTML={{
                            __html: sanitizeNoticeHtml(notice.message),
                        }}
                    />
                </Text>
            </Flex>
        </Box>
    );
}
